// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import loadPages from "@mcmc/App/Handlers/CommonHandlers/LoadPages";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getPagesByNodeIds } from "@mcmc/App/Store/Selectors/ContentSelectors";
import styles from "@mcmc/App/Widgets/RelatedServices/RelatedServices.style";
import { createSectionTitleWidget } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import LazyImage from "@mcmc/MobiusOverrides/LazyImage";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { noResultsMessage, spinner } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    links = "links",
    title = "title",
    titleLink = "titleLink",
    titleLinkLabel = "titleLinkLabel",
}

interface RelatedServiceWidgetProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.titleLinkLabel]: string;
        [fields.links]: LinkModel[];
    };
}

const mapStateToProps = (state: McMcApplicationState, ownProps: RelatedServiceWidgetProps) => {
    return {
        pages: getPagesByNodeIds(
            state,
            ownProps.fields.links.map(link => link.fields.destination.value),
        ),
    };
};

const mapDispatchToProps = {
    loadPages,
};

type Props = RelatedServiceWidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RelatedServicesWidget: React.FC<Props> = ({
    fields: { title, titleLinkLabel, titleLink, links },
    loadPages,
    pages,
}) => {
    const [sectionTitle, setSectionTitle] = useState(
        createSectionTitleWidget(title, titleLink, titleLinkLabel, styles.sectionTitle),
    );

    useEffect(() => {
        setSectionTitle(createSectionTitleWidget(title, titleLink, titleLinkLabel, styles.sectionTitle));
    }, [title, titleLink, titleLinkLabel]);

    useEffect(() => {
        loadPages({ nodeIds: links.map(link => link.fields.destination.value) });
    }, [links]);

    return (
        <StyledBox {...styles.wrapper}>
            {sectionTitle}
            <StyledWrapper {...styles.pagesWrapper}>
                {pages &&
                    pages.length > 0 &&
                    pages.map(page => (
                        <StyledWrapper key={page.id} {...styles.linkWrapper}>
                            <Link
                                href={page.fields.customFullUrl || page.fields.urlSegment}
                                {...styles.link}
                                aria-label={page.fields.title}
                            >
                                {page.fields.thumbnail && (
                                    <LazyImage
                                        src={page.fields.thumbnail}
                                        altText={page.fields.title}
                                        {...styles.image}
                                    />
                                )}
                                {!page.fields.thumbnail && (
                                    <StyledWrapper aria-hidden="true" {...styles.noImageWrapper}>
                                        <Icon {...styles.noImageIcon} />
                                    </StyledWrapper>
                                )}
                                <Typography {...styles.heading}>{page.fields.title}</Typography>
                            </Link>
                        </StyledWrapper>
                    ))}
                {(!pages || pages.length === 0) && (
                    <Typography {...noResultsMessage}>
                        <EllipsisSpinner
                            label={!pages ? translate("Loading") : translate("No Items Available.")}
                            showSpinner={!pages}
                            typographyProps={spinner}
                        />
                    </Typography>
                )}
            </StyledWrapper>
        </StyledBox>
    );
};

export const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.title,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.titleLink,
            fieldType: "Contextual",
            editorTemplate: "LinkField",
            defaultValue: {
                value: "",
                type: "Page",
            },
            isRequired: true,
            validate: (link: LinkFieldValue) => {
                if (!link || !link.value) {
                    return "A value must be provided.";
                }
                return null;
            },
        },
        {
            name: fields.titleLinkLabel,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.links,
            editorTemplate: "ListField",
            getDisplay: (link: HasFields, state) => {
                const { type, value } = link.fields.destination;
                if (type === "Page") {
                    const link = getPageLinkByNodeId(state, value);
                    return link?.title;
                }
                if (type === "Category") {
                    return value; // TODO ISC-10781 make this work
                }
                if (type === "Url") {
                    return value;
                }
                return value;
            },
            validate: (links: HasFields[]) => {
                if (!links || links.length === 0) {
                    return "At least one page must be added.";
                }
                if (links.length > 4) {
                    return "At max 4 pages can be added.";
                }
                return null;
            },
            defaultValue: [],
            fieldType: "Contextual",
            fieldDefinitions: [
                {
                    name: "destination",
                    displayName: "Page Link",
                    editorTemplate: "LinkField",
                    allowCategories: item => false,
                    allowUrls: item => false,
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                    isRequired: true,
                    validate: (link: LinkFieldValue) => {
                        if (!link || !link.value) {
                            return "A value must be provided.";
                        }
                        return null;
                    },
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RelatedServicesWidget),
    definition,
};

export default widgetModule;
